import React, { useState, useEffect, useMemo, useCallback, useRef, useTransition } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthenticatedEffect } from '../hooks/useAuthenticatedEffect';
import { authenticatedFetch } from '../services/authService';
import NavigationUnified from '../components/NavigationUnified';
import BudowyPanel from '../components/BudowyPanel';
import { useNavigate } from 'react-router-dom';
import { useDate } from '../contexts/DateContext';
import ErrorBoundaryDebug from '../components/ErrorBoundaryDebug';
import { Card } from '@ui';
import { X } from '@ui/icons';
import WeatherWidget from '../components/WeatherWidget';
import MapWidget from '../components/MapWidget';
import KomunikatyWidget from '../components/KomunikatyWidget';
import PompyWidget, { POMPY_TIMELINE_PRESETS, type PompyTimelinePreset } from '../components/PompyWidget';
import CardZamowieniaWidget from '../components/CardZamowieniaWidget';

const CARD_LABELS = {
  cardPogoda: 'Pogoda',
  cardMapa: 'Mapa',
  cardKomunikat: 'Komunikat',
  cardPompy: 'Harmonogram Pomp',
  cardZamowienia: 'Zamówienia'
} as const;

type CardKey = keyof typeof CARD_LABELS;
const CARD_KEYS = Object.keys(CARD_LABELS) as CardKey[];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  const [selectedWezel, setSelectedWezel] = useState<number | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [regions, setRegions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { selectedDate, setSelectedDate } = useDate();
  const [budowyOpen, setBudowyOpen] = useState<boolean>(false);
  const [cardsMenuOpen, setCardsMenuOpen] = useState<boolean>(false);
  const cardsMenuRef = useRef<HTMLDivElement | null>(null);
  const [pompyTimelineWindow, setPompyTimelineWindow] = useState<PompyTimelinePreset>(24);
  const [isCardTransitionPending, startCardTransition] = useTransition();
  const [mapCardLoading, setMapCardLoading] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  
  // Highlight specific order from notification link
  const [highlightZamowienieNumer, setHighlightZamowienieNumer] = useState<number | null>(null);
  
  // Konfiguracja widoczności Cards - inteligentne włączanie/wyłączanie
  const [cardsVisibility, setCardsVisibility] = useState<Record<CardKey, boolean>>({
    cardPogoda: true,
    cardMapa: true,
    cardKomunikat: true,
    cardPompy: true,
    cardZamowienia: true
  });
  
  // Oblicz liczbę widocznych Card w pierwszym rzędzie (dla inteligentnego gridu)
  const visibleTopCards = useMemo(() => {
    let count = 0;
    if (cardsVisibility.cardPogoda) count += 1;
    if (cardsVisibility.cardMapa) count += 1;
    if (cardsVisibility.cardKomunikat) count += 1;
    return count;
  }, [cardsVisibility.cardPogoda, cardsVisibility.cardMapa, cardsVisibility.cardKomunikat]);
  
  const updateCardVisibility = useCallback(
    (cardKey: CardKey, next: boolean | ((current: boolean) => boolean)) => {
      startCardTransition(() => {
        setCardsVisibility(prev => {
          const current = prev[cardKey];
          const resolved = typeof next === 'function' ? (next as (value: boolean) => boolean)(current) : next;
          if (current === resolved) {
            return prev;
          }
          return {
            ...prev,
            [cardKey]: resolved
          };
        });
      });
    },
    [startCardTransition]
  );

  const hideCard = useCallback(
    (cardKey: CardKey) => {
      if (cardKey === 'cardMapa') {
        setMapCardLoading(false);
      }
      updateCardVisibility(cardKey, false);
    },
    [updateCardVisibility]
  );

  const makeCheckboxHandler = useCallback(
    (cardKey: CardKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextChecked = event.target.checked;
      if (cardKey === 'cardMapa') {
        setMapCardLoading(nextChecked && !mapInitialized);
      }
      updateCardVisibility(cardKey, nextChecked);
    },
    [mapInitialized, updateCardVisibility]
  );

  const navigationCardsVisibilityHandler = useCallback(
    (cardKey: string) => makeCheckboxHandler(cardKey as CardKey),
    [makeCheckboxHandler]
  );

  useEffect(() => {
    setMapInitialized(false);
    if (cardsVisibility.cardMapa) {
      setMapCardLoading(true);
    }
  }, [selectedRegion]);
  
  // Zamykanie menu po kliknięciu poza nim - poprawiona wersja z lepszym cleanup
  useEffect(() => {
    if (!cardsMenuOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Sprawdź czy kliknięcie było poza kontenerem menu
      if (cardsMenuRef.current && !cardsMenuRef.current.contains(target)) {
        setCardsMenuOpen(false);
      }
    };
    
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && cardsMenuOpen) {
        setCardsMenuOpen(false);
      }
    };
    
    // Użyj capture phase, aby złapać event przed propagacją
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [cardsMenuOpen]);
  
  // Lista wszystkich Card z etykietami (zgodnie z tytułami Card)
  const cardLabels = CARD_LABELS;
  
  // Konfiguracja widoczności sekcji - łatwe włączanie/wyłączanie
  
  // Scoped CSS: widen CardZamowieniaWidget tabs to ensure aggregates fit, and hide legacy tfoot delete if present.
  const navRegionTabsCss = `
    /* Widen CardZamowieniaWidget tabs to reveal aggregates */
    #home-tabs-scope [role="tablist"] [role="tab"] {
      width: 230px !important;
      min-width: 230px !important;
      max-width: 230px !important;
    }
    
    /* Kompaktowy nagłówek Card - wąski pasek z minimalnym paddingiem */
    #home-tabs-scope .compact-card-header {
      padding: 4px 8px !important;
      min-height: auto !important;
      height: auto !important;
    }
    
    #home-tabs-scope .compact-card-header h3 {
      font-size: 12px !important;
      line-height: 1.2 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    #home-tabs-scope .compact-card-header p {
      display: none !important;
    }
    
    /* Kompaktowy nagłówek dla wszystkich Card w home-tabs-scope */
    #home-tabs-scope [class*="border-b"]:has(h3) {
      padding: 4px 8px !important;
      min-height: auto !important;
      height: auto !important;
    }
    
    #home-tabs-scope [class*="border-b"]:has(h3) h3 {
      font-size: 12px !important;
      line-height: 1.2 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    #home-tabs-scope [class*="border-b"]:has(h3) p {
      display: none !important;
    }
    
    /* CardPogoda - ustaw taką samą wysokość jak inne Cards w wierszu */
    #home-tabs-scope .card-pogoda-wrapper {
      height: 160px !important;
    }
    
    /* Wyłącz flex-1 z content div w CardPogoda */
    #home-tabs-scope .card-pogoda-wrapper > div[class*="flex-1"] {
      flex: none !important;
      height: 100% !important;
    }
  `;

  const pompyTitle = (
    <span className="flex items-center gap-2">
      <span>Harmonogram Pomp</span>
      <span className="flex items-center gap-1 text-xs text-gray-600">
        {POMPY_TIMELINE_PRESETS.map((hours) => {
          const isActive = pompyTimelineWindow === hours;
          return (
            <button
              key={hours}
              type="button"
              onClick={() => setPompyTimelineWindow(hours)}
              aria-pressed={isActive}
              className="cursor-pointer"
              style={{
                padding: '2px 8px',
                border: 'none',
                background: 'transparent',
                color: '#374151',
                fontWeight: isActive ? 700 : 500,
                boxShadow: isActive ? 'inset 0 1px 2px rgba(0,0,0,0.25)' : 'none',
                transform: isActive ? 'translateY(1px)' : 'none',
                transition: 'all 0.15s ease',
                minWidth: '40px'
              }}
            >
              {hours}h
            </button>
          );
        })}
      </span>
    </span>
  );

  // Handle URL params when opening page (route, region_id, wezel_id, zamowienie_numer from notification links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const route = params.get('route');
    if (route === 'admin') navigate('/admin', { replace: true });
    if (route === 'surowce') navigate('/surowce', { replace: true });
    
    // Handle notification link params
    const regionId = params.get('region_id');
    const wezelId = params.get('wezel_id');
    const zamowienieNumer = params.get('zamowienie_numer');
    
    if (regionId) {
      const rid = parseInt(regionId, 10);
      if (!isNaN(rid)) setSelectedRegion(rid);
    }
    if (wezelId) {
      const wid = parseInt(wezelId, 10);
      if (!isNaN(wid)) setSelectedWezel(wid);
    }
    if (zamowienieNumer) {
      const znum = parseInt(zamowienieNumer, 10);
      if (!isNaN(znum)) setHighlightZamowienieNumer(znum);
    }
    
    // Clean URL params after reading them (optional - keeps URL clean)
    if (regionId || wezelId || zamowienieNumer) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [navigate]);

  useAuthenticatedEffect(() => {
    console.log('[HomePage] loading regions and wezels');
    loadRegionsAndWezels();
  }, []);

  // Auto-select region: pick first from API response at startup
  useEffect(() => {
    if (regions.length > 0 && !selectedRegion && !selectedWezel) {
      try {
        setSelectedRegion(regions[0].id);
      } catch {}
    }
  }, [regions, selectedRegion, selectedWezel]);

  const loadRegionsAndWezels = async () => {
    try {
      setLoading(true);
      // Load user's assigned regions using cookie-based session
      const response = await authenticatedFetch('/api/auth/user-regions', { credentials: 'include' });
      console.log('[HomePage] received regions and wezels:', response);
      if (response.ok) {
        const regionsData = await response.json();
        const list = regionsData.regions || [];
        setRegions(list);
        // Select default region immediately if not set
        if (!selectedRegion && Array.isArray(list) && list.length > 0) {
          setSelectedRegion(list[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading regions and wezels:', error);
    } finally {
      setLoading(false);
    }
  };

  // Removed extra fetch for user-assigned region; use the list returned by user-regions



  // Removed old region button tagging; NavigationUnified now renders pill buttons natively

  // Removed overlay re-parenting for filter panel to preserve React event handling

  // Removed legacy mirror delete button logic; CardZamowieniaWidget renders footer action natively

  if (loading) {
    return (
    <div id="home-tabs-scope" className="min-h-screen bg-bg text-text flex flex-col">
        <style>{navRegionTabsCss}</style>
        <ErrorBoundaryDebug name="NavigationUnified">
          <NavigationUnified 
            selectedRegion={selectedRegion}
            selectedWezel={selectedWezel}
            selectedDate={selectedDate}
            onRegionChange={(regionId) => setSelectedRegion(regionId)}
            onWezelChange={(wezelId) => setSelectedWezel(wezelId)}
            onDateChange={(date) => setSelectedDate(date)}
          />
        </ErrorBoundaryDebug>
      <div className="flex-1">
      {/* Cards - poniżej TopBar, 3 w jednym wierszu */}
        {visibleTopCards > 0 && (
          <div className="px-5 py-3">
            <div 
              className={`grid gap-4 items-start ${
                visibleTopCards === 1 ? 'grid-cols-1' :
                visibleTopCards === 2 ? 'grid-cols-1 md:grid-cols-2' :
                'grid-cols-1 md:grid-cols-3'
              }`}
            >
              {cardsVisibility.cardPogoda && (
                <Card 
                  title="Pogoda"
                  headerActions={
                    <button
                      onClick={() => hideCard('cardPogoda')}
                      className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                      aria-label="Ukryj CardPogoda"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  }
                  noPadding={true}
                  className="self-start card-pogoda-wrapper"
                >
                  <WeatherWidget selectedRegion={selectedRegion} selectedWezel={null} hideHeader={true} />
                </Card>
              )}
              <div
                className="self-start"
                style={{ display: cardsVisibility.cardMapa ? undefined : 'none' }}
              >
                <Card 
                  title="Mapa"
                  headerActions={
                    <button
                      onClick={() => hideCard('cardMapa')}
                      className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                      aria-label="Ukryj CardMapa"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  }
                  noPadding={true}
                >
                  <MapWidget
                    selectedRegion={selectedRegion}
                    hideHeader={true}
                    externalLoading={mapCardLoading}
                    onReady={() => {
                      setMapInitialized(true);
                      setMapCardLoading(false);
                    }}
                  />
                </Card>
              </div>
              {cardsVisibility.cardKomunikat && (
                <Card 
                  title="Komunikat"
                  headerActions={
                    <button
                      onClick={() => hideCard('cardKomunikat')}
                      className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                      aria-label="Ukryj CardKomunikat"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  }
                  noPadding={true}
                  className="self-start"
                >
                  <KomunikatyWidget selectedRegion={selectedRegion} selectedDate={selectedDate} hideHeader={true} />
                </Card>
              )}
            </div>
          </div>
        )}
        {/* CardPompy - na całą szerokość */}
        {cardsVisibility.cardPompy && (
          <div className="px-5 pb-3">
          <Card 
            title={pompyTitle}
            headerActions={
              <button
                onClick={() => hideCard('cardPompy')}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                aria-label="Ukryj CardPompy"
              >
                <X className="w-4 h-4" />
              </button>
            }
            noPadding={true}
          >
            <PompyWidget
              selectedRegion={selectedRegion}
              selectedDate={selectedDate}
              hideHeader={true}
              timelineWindowHours={pompyTimelineWindow}
            />
            </Card>
          </div>
        )}
        {/* CardZamowienia - na całą szerokość */}
        {cardsVisibility.cardZamowienia && (
          <div className="px-5 pb-3">
            <Card 
              title="Zamówienia"
              headerActions={
                <button
                  onClick={() => hideCard('cardZamowienia')}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  aria-label="Ukryj CardZamowienia"
                >
                  <X className="w-4 h-4" />
                </button>
              }
            >
              <div>Zawartość CardZamowienia</div>
            </Card>
          </div>
        )}
        
        <div className="flex-1 max-w-5xl mx-auto p-8">
          <div className="bg-card border border-border rounded-lg shadow-sm p-6 my-4">
            <div className="flex justify-center p-8">
              <div className="w-8 h-8 border-2 border-border border-t-accent rounded-full animate-spin"></div>
            </div>
          </div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div id="home-tabs-scope" className="min-h-screen bg-bg text-text flex flex-col">
      <style>{navRegionTabsCss}</style>
      <ErrorBoundaryDebug name="NavigationUnified">
        <NavigationUnified 
          selectedRegion={selectedRegion}
          selectedWezel={selectedWezel}
          selectedDate={selectedDate}
          onRegionChange={(regionId) => setSelectedRegion(regionId)}
          onWezelChange={(wezelId) => setSelectedWezel(wezelId)}
          onDateChange={(date) => setSelectedDate(date)}
          cardsVisibility={cardsVisibility}
          onCardsVisibilityChange={navigationCardsVisibilityHandler}
          cardsMenuOpen={cardsMenuOpen}
          onCardsMenuToggle={() => setCardsMenuOpen(!cardsMenuOpen)}
          cardsMenuRef={cardsMenuRef}
          isCardTransitionPending={isCardTransitionPending}
          cardLabels={cardLabels}
        />
      </ErrorBoundaryDebug>
      <div className="flex-1">
      {/* Cards - poniżej TopBar, 3 w jednym wierszu */}
      {visibleTopCards > 0 && (
        <div className="px-5 py-3">
          <div 
            className={`grid gap-4 items-start ${
              visibleTopCards === 1 ? 'grid-cols-1' :
              visibleTopCards === 2 ? 'grid-cols-1 md:grid-cols-2' :
              'grid-cols-1 md:grid-cols-3'
            }`}
          >
            {cardsVisibility.cardPogoda && (
              <Card 
                title="Pogoda"
                headerActions={
                  <button
                    onClick={() => hideCard('cardPogoda')}
                    className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                    aria-label="Ukryj CardPogoda"
                  >
                    <X className="w-4 h-4" />
                  </button>
                }
                noPadding={true}
                className="self-start card-pogoda-wrapper"
              >
                <WeatherWidget selectedRegion={selectedRegion} selectedWezel={null} hideHeader={true} />
              </Card>
            )}
            <div
              className="self-start"
              style={{ display: cardsVisibility.cardMapa ? undefined : 'none' }}
            >
              <Card 
                title="Mapa"
                headerActions={
                  <button
                    onClick={() => hideCard('cardMapa')}
                    className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                    aria-label="Ukryj CardMapa"
                  >
                    <X className="w-4 h-4" />
                  </button>
                }
                noPadding={true}
              >
                <MapWidget
                  selectedRegion={selectedRegion}
                  hideHeader={true}
                  externalLoading={mapCardLoading}
                  onReady={() => {
                    setMapInitialized(true);
                    setMapCardLoading(false);
                  }}
                />
              </Card>
            </div>
            {cardsVisibility.cardKomunikat && (
              <Card 
                title="Komunikat"
                headerActions={
                  <button
                    onClick={() => hideCard('cardKomunikat')}
                    className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                    aria-label="Ukryj CardKomunikat"
                  >
                    <X className="w-4 h-4" />
                  </button>
                }
                noPadding={true}
                className="self-start"
              >
                <KomunikatyWidget selectedRegion={selectedRegion} selectedDate={selectedDate} hideHeader={true} />
              </Card>
            )}
          </div>
        </div>
      )}
      {/* CardPompy - na całą szerokość */}
      {cardsVisibility.cardPompy && (
        <div className="px-5 pb-3">
          <Card 
            title={pompyTitle}
            headerActions={
              <button
                onClick={() => hideCard('cardPompy')}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                aria-label="Ukryj CardPompy"
              >
                <X className="w-4 h-4" />
              </button>
            }
            noPadding={true}
          >
            <PompyWidget
              selectedRegion={selectedRegion}
              selectedDate={selectedDate}
              hideHeader={true}
              timelineWindowHours={pompyTimelineWindow}
            />
          </Card>
        </div>
      )}
      {/* CardZamowienia - na całą szerokość - BEZ Card wrapper i BEZ wrapper div */}
      {cardsVisibility.cardZamowienia && (
        <CardZamowieniaWidget
          selectedRegion={selectedRegion}
          selectedWezel={selectedWezel}
          selectedDate={selectedDate}
          highlightZamowienieNumer={highlightZamowienieNumer}
          onHighlightClear={() => setHighlightZamowienieNumer(null)}
        />
      )}
      
      <BudowyPanel visible={budowyOpen} onClose={() => setBudowyOpen(false)} selectedRegion={selectedRegion} />
      </div>
    </div>
  );
};

export default HomePage;